import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { TimeEntry } from '../../types';
import { apiService } from '../../services/api';

// Types for time entry state
export interface TimeEntryFilters {
  dateRange?: {
    start: string;
    end: string;
  };
  projectId?: string;
  retainerId?: string;
  userId?: string;
  podId?: string;
  taskType?: string;
  billable?: boolean;
  approved?: boolean;
  dataSource?: 'jira' | 'clickup' | 'manual';
}

export interface TimeEntryStats {
  todayHours: number;
  weekHours: number;
  monthHours: number;
  pendingApprovals: number;
  billablePercentage: number;
  totalEntries: number;
}

export interface TimeEntriesState {
  entries: TimeEntry[];
  loading: boolean;
  error: string | null;
  filters: TimeEntryFilters;
  stats: TimeEntryStats;
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
  selectedEntries: string[];
  activeView: 'list' | 'calendar' | 'analytics';
}

const initialState: TimeEntriesState = {
  entries: [],
  loading: false,
  error: null,
  filters: {},
  stats: {
    todayHours: 0,
    weekHours: 0,
    monthHours: 0,
    pendingApprovals: 0,
    billablePercentage: 0,
    totalEntries: 0
  },
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    hasMore: false
  },
  selectedEntries: [],
  activeView: 'list'
};

// Async thunks for API operations
export const fetchTimeEntries = createAsyncThunk(
  'timeEntries/fetchTimeEntries',
  async (params: { filters?: TimeEntryFilters; page?: number; limit?: number } = {}) => {
    const { filters = {}, page = 1, limit = 50 } = params;
    const response = await apiService.getTimeEntries({
      ...filters,
      page,
      limit
    });
    return response.result as unknown as { results: TimeEntry[]; total: number; page: number; limit: number };
  }
);

export const fetchTimeEntryStats = createAsyncThunk(
  'timeEntries/fetchStats',
  async (filters: TimeEntryFilters = {}) => {
    // This would be a separate API endpoint for stats
    // For now, we'll calculate basic stats from the entries
    const response = await apiService.getTimeEntries({ ...filters, limit: 1000 });
    const responseData = response.result as unknown as { results: TimeEntry[] };
    const entries = responseData.results || [];

    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay())).toISOString().split('T')[0];
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];

    const todayEntries = entries.filter((entry: TimeEntry) => entry.date.startsWith(today));
    const weekEntries = entries.filter((entry: TimeEntry) => entry.date >= weekStart);
    const monthEntries = entries.filter((entry: TimeEntry) => entry.date >= monthStart);
    const pendingEntries = entries.filter((entry: TimeEntry) => !entry.approved);
    const billableEntries = entries.filter((entry: TimeEntry) => entry.billable);

    return {
      todayHours: todayEntries.reduce((sum: number, entry: TimeEntry) => sum + entry.hours, 0),
      weekHours: weekEntries.reduce((sum: number, entry: TimeEntry) => sum + entry.hours, 0),
      monthHours: monthEntries.reduce((sum: number, entry: TimeEntry) => sum + entry.hours, 0),
      pendingApprovals: pendingEntries.length,
      billablePercentage: entries.length > 0 ? Math.round((billableEntries.length / entries.length) * 100) : 0,
      totalEntries: entries.length
    };
  }
);

export const createTimeEntry = createAsyncThunk(
  'timeEntries/createTimeEntry',
  async (timeEntryData: Partial<TimeEntry>) => {
    const response = await apiService.createTimeEntry(timeEntryData);
    return response.result as unknown as TimeEntry;
  }
);

export const updateTimeEntry = createAsyncThunk(
  'timeEntries/updateTimeEntry',
  async ({ id, data }: { id: string; data: Partial<TimeEntry> }) => {
    const response = await apiService.updateTimeEntry(id, data);
    return response.result as unknown as TimeEntry;
  }
);

export const deleteTimeEntry = createAsyncThunk(
  'timeEntries/deleteTimeEntry',
  async (id: string) => {
    await apiService.deleteTimeEntry(id);
    return id;
  }
);

// Time entries slice
const timeEntriesSlice = createSlice({
  name: 'timeEntries',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TimeEntryFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset pagination when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setActiveView: (state, action: PayloadAction<'list' | 'calendar' | 'analytics'>) => {
      state.activeView = action.payload;
    },
    selectTimeEntry: (state, action: PayloadAction<string>) => {
      const entryId = action.payload;
      if (!state.selectedEntries.includes(entryId)) {
        state.selectedEntries.push(entryId);
      }
    },
    deselectTimeEntry: (state, action: PayloadAction<string>) => {
      const entryId = action.payload;
      state.selectedEntries = state.selectedEntries.filter(id => id !== entryId);
    },
    selectAllTimeEntries: (state) => {
      state.selectedEntries = state.entries.map(entry => entry.id);
    },
    clearSelection: (state) => {
      state.selectedEntries = [];
    },
    setPagination: (state, action: PayloadAction<{ page?: number; limit?: number }>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch time entries
      .addCase(fetchTimeEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload.data || [];
        state.pagination = {
          ...state.pagination,
          total: action.payload.total || 0,
          hasMore: (action.payload.data?.length || 0) === state.pagination.limit
        };
      })
      .addCase(fetchTimeEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch time entries';
      })
      
      // Fetch stats
      .addCase(fetchTimeEntryStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      
      // Create time entry
      .addCase(createTimeEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTimeEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.entries.unshift(action.payload);
        state.stats.totalEntries += 1;
      })
      .addCase(createTimeEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create time entry';
      })
      
      // Update time entry
      .addCase(updateTimeEntry.fulfilled, (state, action) => {
        const index = state.entries.findIndex(entry => entry.id === action.payload.id);
        if (index !== -1) {
          state.entries[index] = action.payload;
        }
      })
      
      // Delete time entry
      .addCase(deleteTimeEntry.fulfilled, (state, action) => {
        state.entries = state.entries.filter(entry => entry.id !== action.payload);
        state.selectedEntries = state.selectedEntries.filter(id => id !== action.payload);
        state.stats.totalEntries = Math.max(0, state.stats.totalEntries - 1);
      });
  }
});

export const {
  setFilters,
  clearFilters,
  setActiveView,
  selectTimeEntry,
  deselectTimeEntry,
  selectAllTimeEntries,
  clearSelection,
  setPagination,
  clearError
} = timeEntriesSlice.actions;

export default timeEntriesSlice.reducer;
